import { forwardRef, ForwardedRef } from 'react';
import styled, { CSSProperties } from 'styled-components';
import { pxToRem } from 'utils/convert';

export type RenderCustomTruncateProps = {
  children: React.ReactNode;
  /** Ref to be passed to the custom react element ref */
  ref?: ForwardedRef<any>;
  /** Style property containing the max-width attribute */
  style: CSSProperties;
  /** Data attribute used to apply truncation styling. This must exist on the custom element being truncated. */
  truncateAttribute: { [key: string]: string };
};

export type TruncateProps = {
  /** Max width of element before truncation in pixels */
  maxWidth?: number;
  /** Content to check for truncation */
  children: React.ReactNode;
  /** Render prop for display custom content */
  renderCustomContent?: (props: RenderCustomTruncateProps) => React.ReactNode;
};

const truncateStyling = `
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const CustomTruncateContainer = styled.div`
  & * {
    &[data-truncate] {
      ${truncateStyling}
    }
  }
`;

const TruncateText = styled.div`
  &[data-truncate] {
    ${truncateStyling}
  }
`;

export const Truncate = forwardRef(
  (
    { maxWidth = 100, children, renderCustomContent, ...props }: TruncateProps,
    ref?: ForwardedRef<any>,
  ) => {
    const widthAttribute = maxWidth ? { maxWidth: pxToRem(maxWidth) } : {};
    if (!renderCustomContent) {
      return (
        <TruncateText ref={ref} {...props} style={widthAttribute} data-truncate>
          {children}
        </TruncateText>
      );
    }

    return (
      <CustomTruncateContainer>
        {renderCustomContent({
          ref,
          truncateAttribute: {
            'data-truncate': '',
          },
          style: widthAttribute,
          children,
        })}
      </CustomTruncateContainer>
    );
  },
);
